import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Room, RoomEvent, Track, createLocalTracks } from "livekit-client";
import { api, formatApiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UserAvatar, VerifiedBadge } from "../components/UserAvatar";
import { toast } from "sonner";
import { Mic, MicOff, Video, VideoOff, MonitorUp, MonitorOff, Send, Coins, LogOut, Radio, Eye, X, Play } from "lucide-react";

export default function Live() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stream, setStream] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | prejoin | live | ended | notfound
  const [isOwner, setIsOwner] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenOn, setScreenOn] = useState(false);
  const [viewers, setViewers] = useState(0);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [coins, setCoins] = useState(0);
  const [showCoin, setShowCoin] = useState(false);
  const [coinAmt, setCoinAmt] = useState(50);
  const [overlay, setOverlay] = useState([]);
  const [totalCoins, setTotalCoins] = useState(0);

  const roomRef = useRef(null);
  const videoWrapRef = useRef(null);
  const audioWrapRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    api.get(`/live/${id}`).then(({ data }) => {
      setStream(data);
      setIsOwner(!!data.is_owner);
      setTotalCoins(data.total_coins || 0);
      setStatus(data.status === "live" ? "prejoin" : "ended");
    }).catch(() => setStatus("notfound"));
    api.get("/wallet").then(({ data }) => setCoins(data.coin_balance)).catch(() => {});
  }, [id]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  const attachVideo = useCallback((track) => {
    if (!videoWrapRef.current) return;
    const el = track.attach();
    el.className = "w-full h-full object-contain";
    el.setAttribute("data-lk-sid", track.sid || "local");
    videoWrapRef.current.appendChild(el);
  }, []);

  const detachTrack = useCallback((track) => {
    track.detach().forEach((el) => el.remove());
  }, []);

  const pushOverlay = useCallback((amount, fromName) => {
    const key = Math.random().toString(36).slice(2);
    setOverlay((o) => [...o, { key, amount, fromName }]);
    setTimeout(() => setOverlay((o) => o.filter((x) => x.key !== key)), 2000);
  }, []);

  const connect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const { data } = await api.post(`/live/${id}/token`);
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Video) attachVideo(track);
        else if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          audioWrapRef.current?.appendChild(el);
        }
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => detachTrack(track));
      room.on(RoomEvent.LocalTrackPublished, (pub) => { if (pub.track && pub.track.kind === Track.Kind.Video) attachVideo(pub.track); });
      room.on(RoomEvent.LocalTrackUnpublished, (pub) => { if (pub.track) detachTrack(pub.track); });
      room.on(RoomEvent.ParticipantConnected, () => setViewers(room.numParticipants));
      room.on(RoomEvent.ParticipantDisconnected, () => setViewers(room.numParticipants));
      room.on(RoomEvent.Disconnected, () => { setStatus("ended"); });
      room.on(RoomEvent.DataReceived, (payload) => {
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload));
          if (msg.kind === "chat") setChat((c) => [...c, { id: Math.random(), user: msg.user, text: msg.text }]);
          else if (msg.kind === "coin") { setChat((c) => [...c, { id: Math.random(), user: msg.from, text: `${msg.amount} eCoin gönderdi!`, coin: true }]); pushOverlay(msg.amount, msg.from); setTotalCoins((t) => t + msg.amount); }
        } catch {}
      });

      await room.connect(data.url, data.token);
      setViewers(room.numParticipants);

      if (data.is_owner) {
        try { await room.localParticipant.setCameraEnabled(true); setCamOn(true); }
        catch (err) { setCamOn(false); toast.error("Kameraya erişilemedi (izin verildi mi?)"); }
        try { await room.localParticipant.setMicrophoneEnabled(true); setMicOn(true); }
        catch (err) { setMicOn(false); }
      }
      setStatus("live");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Yayına bağlanılamadı");
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => () => { try { roomRef.current?.disconnect(); } catch {} }, []);

  const toggleMic = async () => {
    const room = roomRef.current; if (!room) return;
    const on = !micOn; await room.localParticipant.setMicrophoneEnabled(on); setMicOn(on);
  };
  const toggleCam = async () => {
    const room = roomRef.current; if (!room) return;
    const on = !camOn;
    try { await room.localParticipant.setCameraEnabled(on); setCamOn(on); }
    catch { toast.error("Kameraya erişilemedi"); }
  };
  const toggleScreen = async () => {
    const room = roomRef.current; if (!room) return;
    const on = !screenOn;
    try {
      await room.localParticipant.setScreenShareEnabled(on, { audio: true });
      setScreenOn(on);
    } catch (e) {
      toast.error("Ekran paylaşımı başlatılamadı");
    }
  };

  const publishData = (obj) => {
    try {
      roomRef.current?.localParticipant?.publishData(new TextEncoder().encode(JSON.stringify(obj)), { reliable: true });
    } catch {}
  };

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text) return;
    const uname = user?.username || "misafir";
    publishData({ kind: "chat", user: uname, text });
    setChat((c) => [...c, { id: Math.random(), user: uname, text, me: true }]);
    setChatInput("");
  };

  const sendCoin = async () => {
    const amount = parseInt(coinAmt, 10);
    if (!amount || amount < 1) { toast.error("Geçerli miktar girin"); return; }
    try {
      const { data } = await api.post(`/live/${id}/coin`, { amount });
      const uname = user?.username || "misafir";
      setCoins(data.coin_balance);
      publishData({ kind: "coin", from: uname, amount });
      pushOverlay(amount, uname);
      setTotalCoins(data.total_coins ?? (totalCoins + amount));
      setChat((c) => [...c, { id: Math.random(), user: uname, text: `${amount} eCoin gönderdi!`, coin: true, me: true }]);
      setShowCoin(false);
      toast.success(`${amount} eCoin gönderildi!`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const stopStream = async () => {
    if (!window.confirm("Yayını sonlandırmak istiyor musun?")) return;
    try { await api.post(`/live/${id}/stop`); } catch {}
    try { roomRef.current?.disconnect(); } catch {}
    toast.success("Yayın sonlandırıldı");
    navigate("/");
  };
  const leave = () => { try { roomRef.current?.disconnect(); } catch {} navigate("/"); };

  if (status === "loading") return <div className="min-h-screen bg-black flex items-center justify-center text-gray-500">Yükleniyor...</div>;
  if (status === "notfound") return <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">Yayın bulunamadı.</div>;
  if (status === "ended") return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-4">
      <Radio className="text-gray-600 mb-4" size={48} />
      <h2 className="text-2xl font-bold text-white">Yayın sona erdi</h2>
      <button onClick={() => navigate("/")} className="mt-6 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-2.5 rounded-md">Ana Sayfa</button>
    </div>
  );

  if (status === "prejoin") return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-4 relative overflow-hidden">
      {stream?.image_url && <img src={mediaUrl(stream.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
      <div className="relative z-10 flex flex-col items-center">
      <div className="flex items-center gap-2 mb-4"><span className="w-2.5 h-2.5 rounded-full bg-[#E50914] animate-pulse" /><span className="text-[#E50914] font-bold uppercase text-sm tracking-wide">Canlı</span></div>
      {stream?.broadcaster && <UserAvatar user={stream.broadcaster} className="h-20 w-20 mb-3" />}
      <h1 className="text-2xl sm:text-3xl font-black text-white flex items-center gap-2">{stream?.title} {stream?.broadcaster?.verified && <VerifiedBadge size={20} />}</h1>
      <p className="text-gray-400 mt-1">@{stream?.broadcaster?.username} • {stream?.category}</p>
      {stream?.goal > 0 && (
        <div className="w-72 mt-5" data-testid="live-goal-bar">
          <div className="flex justify-between text-xs text-gray-300 mb-1"><span className="flex items-center gap-1"><img src="/ecoin.png" alt="" className="h-4 w-4" /> Hedef</span><span>{totalCoins} / {stream.goal}</span></div>
          <div className="h-2.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#ff8a00] to-[#E50914] transition-all" style={{ width: `${Math.min(100, (totalCoins / stream.goal) * 100)}%` }} /></div>
        </div>
      )}
      <button data-testid="live-join-button" onClick={connect} disabled={connecting}
        className="mt-8 inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-bold px-8 py-3.5 rounded-full transition-all active:scale-95 disabled:opacity-60">
        {isOwner ? <><Radio size={20} /> {connecting ? "Başlatılıyor..." : "Yayını Başlat"}</> : <><Play size={20} className="fill-white" /> {connecting ? "Bağlanılıyor..." : "İzle"}</>}
      </button>
      </div>
    </div>
  );

  // status === live
  return (
    <div className="fixed inset-0 bg-black flex flex-col md:flex-row">
      {/* Video area */}
      <div className="relative flex-1 min-h-0 bg-black">
        <div ref={videoWrapRef} className="absolute inset-0 flex items-center justify-center gap-1 [&>video]:max-h-full" data-testid="live-video" />
        <div ref={audioWrapRef} className="hidden" />

        {/* top bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-3 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center gap-2">
            {stream?.broadcaster && <UserAvatar user={stream.broadcaster} className="h-9 w-9" />}
            <div>
              <div className="text-white text-sm font-semibold flex items-center gap-1">@{stream?.broadcaster?.username} {stream?.broadcaster?.verified && <VerifiedBadge size={13} />}</div>
              <div className="flex items-center gap-1.5"><span className="bg-[#E50914] text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />CANLI</span><span className="text-gray-300 text-xs flex items-center gap-1"><Eye size={12} /> {viewers}</span></div>
            </div>
          </div>
          <button data-testid="live-exit" onClick={isOwner ? stopStream : leave} className="text-white/80 hover:text-white bg-black/40 rounded-full p-2"><X size={20} /></button>
        </div>

        {/* goal bar */}
        {stream?.goal > 0 && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 w-64 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5" data-testid="live-goal-bar-active">
            <div className="flex justify-between text-[10px] text-gray-200 mb-1"><span className="flex items-center gap-1"><img src="/ecoin.png" alt="" className="h-3 w-3" /> Hedef</span><span>{totalCoins}/{stream.goal}</span></div>
            <div className="h-1.5 bg-white/15 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#ff8a00] to-[#E50914] transition-all" style={{ width: `${Math.min(100, (totalCoins / stream.goal) * 100)}%` }} /></div>
          </div>
        )}

        {/* gift overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {overlay.map((o) => (
            <div key={o.key} className="gift-float absolute left-1/2 -translate-x-1/2 bottom-24 flex flex-col items-center">
              <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full pl-2 pr-4 py-2 border border-[#E50914]/40">
                <img src="/ecoin.png" alt="eCoin" className="h-12 w-12 object-contain drop-shadow-[0_0_16px_rgba(229,9,20,0.8)]" />
                <div className="text-left">
                  <div className="text-white font-black text-lg leading-none">{o.amount} eCoin</div>
                  <div className="text-gray-300 text-xs mt-0.5">@{o.fromName} gönderdi</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* owner controls */}
        {isOwner && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/50 backdrop-blur-sm rounded-full px-4 py-2.5">
            <button data-testid="live-mic" onClick={toggleMic} className={`p-2.5 rounded-full ${micOn ? "bg-white/15 text-white" : "bg-[#E50914] text-white"}`}>{micOn ? <Mic size={20} /> : <MicOff size={20} />}</button>
            <button data-testid="live-cam" onClick={toggleCam} className={`p-2.5 rounded-full ${camOn ? "bg-white/15 text-white" : "bg-[#E50914] text-white"}`}>{camOn ? <Video size={20} /> : <VideoOff size={20} />}</button>
            <button data-testid="live-screen" onClick={toggleScreen} className={`p-2.5 rounded-full ${screenOn ? "bg-[#1d9bf0] text-white" : "bg-white/15 text-white"}`}>{screenOn ? <MonitorOff size={20} /> : <MonitorUp size={20} />}</button>
            <button data-testid="live-stop" onClick={stopStream} className="p-2.5 rounded-full bg-[#E50914] text-white"><LogOut size={20} /></button>
          </div>
        )}
      </div>

      {/* Chat + gifts sidebar */}
      <div className="w-full md:w-80 shrink-0 bg-[#111] border-t md:border-t-0 md:border-l border-white/10 flex flex-col h-56 md:h-auto">
        <div className="px-3 py-2 border-b border-white/10 text-white text-sm font-semibold flex items-center gap-2"><Radio size={15} className="text-[#E50914]" /> Canlı Sohbet</div>
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-1.5" data-testid="live-chat">
          {chat.map((m) => (
            <div key={m.id} className="text-sm">
              <span className={`font-semibold ${m.coin ? "text-[#E50914]" : "text-[#1d9bf0]"}`}>@{m.user}</span>{" "}
              <span className="text-gray-200">{m.coin && "🪙 "}{m.text}</span>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        {showCoin && (
          <div className="border-t border-white/10 p-3" data-testid="live-coin-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-sm text-gray-300"><img src="/ecoin.png" alt="" className="h-5 w-5" /> Bakiye: <span className="text-white font-semibold">{coins}</span></div>
              <button onClick={() => navigate(`/u/${user?.username}`)} className="text-xs text-[#E50914] hover:underline">Yükle</button>
            </div>
            <div className="flex gap-1.5 mb-2">
              {[10, 50, 100, 500].map((v) => (
                <button key={v} data-testid={`coin-quick-${v}`} onClick={() => setCoinAmt(v)} className={`flex-1 py-1.5 rounded text-xs font-semibold ${Number(coinAmt) === v ? "bg-[#E50914] text-white" : "bg-white/10 text-gray-300"}`}>{v}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="coin-amount-input" type="number" min="1" value={coinAmt} onChange={(e) => setCoinAmt(e.target.value)} className="flex-1 bg-[#2B2B2B] border border-white/10 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#E50914]" />
              <button data-testid="coin-send-confirm" onClick={sendCoin} className="bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-4 rounded text-sm">Gönder</button>
            </div>
          </div>
        )}
        <div className="p-2 border-t border-white/10 flex items-center gap-2">
          <button data-testid="live-coin-toggle" onClick={() => setShowCoin((s) => !s)} className={`p-2 rounded-full ${showCoin ? "bg-[#E50914] text-white" : "bg-white/10 text-[#E50914]"}`}><Coins size={18} /></button>
          <input data-testid="live-chat-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Mesaj yaz..." className="flex-1 bg-[#2B2B2B] border border-white/10 text-white rounded-full px-3 py-2 text-sm focus:outline-none focus:border-[#E50914]" />
          <button data-testid="live-chat-send" onClick={sendChat} className="p-2 rounded-full bg-[#E50914] text-white"><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}
